/**
 * Catalogue search + pick, used for "change / match item" on one line and for
 * "match selected → item" in bulk (prototype `openCatalog` / `openCatalogForMerge`).
 *
 * The search box queries `GET /items?q=` — server-side, because the real catalogue is
 * large and the production endpoint runs vector search (so "gear oil" finds "Marine Gear
 * Oil SAE90"). Results are scoped to the enquiry type's allowed item groups.
 *
 * ---- price and stock ARE shown here now (2026-08-11) ----
 *
 * Each result carries code, description, group, **price** and **stock**, so the choice can be
 * made in the dialog instead of by picking an item and looking at the table afterwards.
 *
 * This reverses part of the Task 8 note kept below, and the reasoning matters because the old
 * objection was RIGHT about the thing it was about. It argued no per-row fetching, on a
 * catalogue of 18,836 items. That still holds. Neither column fetches per row:
 *
 *   price  — ONE `Item Price` query with an `in` filter over every visible code
 *            (`useItemPrices`). A hundred results cost one request.
 *   stock  — ONE probe for whether stock is answerable at all (`useStockEnabled`). It is not,
 *            today, so no row asks anything. If it becomes answerable, rows ask individually and
 *            only up to `STOCK_ROW_CAP`, because no batch stock endpoint exists.
 *
 * The old note also said a blank cell would read as "none exists" when we simply had not asked.
 * That objection is answered by wording, not by omission: an item with no `Item Price` row says
 * **"no price"**, an unasked stock cell says **"— in stock"** with the reason on hover, and
 * neither ever renders `0`. Suppliers and contracts are still not fetched here, and the original
 * argument for that is untouched.
 *
 * ---- the original Task 8 note, kept for its reasoning ----
 *
 * `items.get_items` (the list this modal searches) no longer carries `contracts`,
 * `candidate_suppliers`, `base_price` or `on_hand` — each now lives behind its own per-item
 * lookup (`get_blanket_orders`, `get_suppliers_for_item`, `get_stock`; see `useItems.ts`'s
 * `toItem`). This modal deliberately does NOT fetch any of those per row: the catalogue runs to
 * 18,836 items, and a picker whose every visible row fires three more requests is an N+1 across
 * the whole thing, not a page. The columns are dropped from the list entirely rather than shown
 * blank — a blank "suppliers:" or "—" price here would read as "no suppliers exist" / "no price
 * exists", which is not a fact this list knows; it just hasn't asked. Once an item is PICKED,
 * the RFQ table it lands in already fetches per-code detail (`useItemsByCode`) and shows the real
 * answer. If a future screen genuinely needs this context inside the picker, the right shape is a
 * fetch on expanding ONE row (hover/click), never on every row in the list.
 */

import { useMemo, useState } from 'react'
import { Modal } from '../../components/Modal'
import { Placeholder } from '../../components/ui'
import { formatMoney } from '../../lib/format'
import { useItemPrices, useItems, useStock, useStockEnabled, type PriceSource } from '../../lib/api/useItems'
import type { MatchCandidate } from '../../lib/searchMatch'
import { matchesAllTerms, pageWasTruncated, searchTerms, serverQueryFor } from '../../lib/catalogSearch'
import { uomOptions } from '../../lib/rfq'
import { suppliersForGroup, useItemGroupSuppliers } from '../../lib/api/useItemGroupSuppliers'
import type { EnquiryType, Item, Money } from '../../lib/types'

/**
 * How many CATALOGUE rows may ask for their own stock.
 *
 * Stock is connected now — `items.get_stock` answers `{stock_enabled: true, qty: 28,
 * warehouse_breakdown: […], reserved_qty: 33}` (measured 2026-08-29), where it used to answer
 * `stock_enabled: false` for everything. So the cap this constant applies stopped being
 * theoretical the day the ERP was wired up.
 *
 * There is still NO batch form, measured the same day and worth stating precisely because one of
 * the failure modes is silent:
 *
 *   item_codes=[…] / items=[…]  → 500 TypeError: get_stock() missing 'item_code'
 *   item_code=["A","B"]         → 200 {"stock_enabled":true,"qty":0}   ← a FALSE ZERO
 *   item_code=A,B               → 200 {"stock_enabled":true,"qty":0}   ← a FALSE ZERO
 *
 * An array or a comma list is accepted and answered with qty 0 rather than an error, which would
 * report every item as out of stock. Never batch this endpoint by hand. There is no local `Bin`
 * doctype either (404) — the figures come from a remote ERP Frappe proxies to — so an `in` filter
 * is not available as a fallback.
 *
 * At ~1.24s a call and six concurrent connections, 30 rows was ~6s of the browser doing nothing
 * else. Lowered to 15: the matcher's shortlist is rendered ABOVE these and gets the connections
 * first, which is the part a purchaser is choosing between. Delete both caps the day a batch
 * lookup exists (asked for as `D17`).
 */
const STOCK_ROW_CAP = 15

/** One page from the server. The client narrows within it — see `lib/catalogSearch.ts`. */
/** The shortlist is at most ten; all of it asks for stock — see `STOCK_ROW_CAP`. */
const SUGGESTED_STOCK_CAP = 10

const CATALOG_PAGE = 100

/**
 * How many units a result row names before it stops listing them.
 *
 * Some items accept 21 units apiece (the `Non stock provisions` family); spelling all of them out
 * turns a search result into a wall of unit codes and pushes the next result off screen. The base
 * unit always survives the truncation — it is the one that governs stock and pricing.
 */
const UNITS_SHOWN = 6

/** "base TRAY · also CASE, PALLET" — the units this item can be ordered in, base named first. */
export function unitsLine(item: Item): string {
  // `uomOptions` leads with the base; naming it twice reads as a duplicate rather than emphasis.
  const others = uomOptions(item)
    .map((c) => c.uom)
    .filter((u) => u !== item.base_uom)
  if (others.length === 0) return `base ${item.base_uom}`
  const rest = others.length - UNITS_SHOWN
  const shown = others.slice(0, UNITS_SHOWN).join(', ')
  return `base ${item.base_uom} · also ${shown}${rest > 0 ? ` +${rest} more` : ''}`
}

export function CatalogPickerModal({
  title,
  intro,
  enquiryType,
  suggested,
  suggestedPending,
  onPick,
  onClose,
}: {
  title: string
  intro?: string
  enquiryType: EnquiryType
  /**
   * The vector matcher's own ranked answers for the line being matched, pinned above the
   * catalogue search.
   *
   * Opening this dialog used to mean typing a search for text the customer had already written,
   * over 18,836 items, with a plain `LIKE` doing the ranking — while the semantic matcher had
   * already produced the likely answers for that exact line. Showing them first turns the common
   * case into one click and leaves the search for when none of them is right.
   */
  suggested?: MatchCandidate[]
  /** The ranked list is being fetched for this line right now — see `RfqPage`'s own note on why
   *  the dialog asks for its own match rather than relying on the table's. */
  suggestedPending?: boolean
  onPick: (item: Item) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  /*
    Multi-word search: the server takes one string and matches it as a substring, so `battery 240`
    would miss `BATTERY 12V 240AH`. The broadest term goes to the server and the rest are applied
    here — see `lib/catalogSearch.ts`.
  */
  const terms = searchTerms(query)
  const serverQuery = serverQueryFor(query)
  /*
    The catalogue is searched only once something is TYPED (Akhil, 2026-09-16). This used to
    fetch the unsearched first page as well and list it under the matcher's shortlist, so the
    dialog opened on the closest matches followed by twenty unrelated items — the "original
    search" nobody asked for. Now it opens on the shortlist alone; a purchaser who wants
    something else types it.
  */
  const searching = query.trim() !== ''
  const { data, isPending, error } = useItems(
    {
      enquiry_type: enquiryType,
      q: serverQuery || undefined,
      limit: CATALOG_PAGE,
    },
    { enabled: searching },
  )
  const returned = searching ? (data?.data ?? []) : []
  const results = useMemo(() => returned.filter((item) => matchesAllTerms(item, terms)), [returned, terms])
  // Rows the extra terms removed, and whether more could exist past the server's page.
  const hiddenByTerms = returned.length - results.length
  const truncated = pageWasTruncated(returned.length, CATALOG_PAGE)

  /*
    ONE request for every visible price (an `in` filter), not one per row — see `useItemPrices`.

    The matcher's suggestions are included in the SAME request rather than fetched separately:
    they are rows in this dialog exactly like search results are, and the whole reason they moved
    here from the RFQ table is so the choice between them can be made on rate and stock. A second
    query for ten more codes would be a second request for no reason.
  */
  const suggestedCodes = useMemo(() => (suggested ?? []).map((c) => c.item_code), [suggested])
  const codes = useMemo(
    () => [...new Set([...results.map((i) => i.code), ...suggestedCodes])],
    [results, suggestedCodes],
  )
  const prices = useItemPrices(codes)
  /*
    Which suppliers can be asked for each row — resolved for FREE.

    Suppliers hang off `Item Group`, not off the item, and `data_ops.get_item_groups_with_
    suppliers` returns the entire mapping in ONE request cached for the session. Every row here
    already carries its `item_group`, so this is a Map lookup per row and not a per-item call.
    Choosing an item nobody can be asked to quote is a dead end the purchaser should see BEFORE
    picking it, not after opening the supplier dialog.
  */
  const groupSuppliers = useItemGroupSuppliers()
  // ONE probe for whether stock can be answered at all, before any row asks.
  const stockEnabled = useStockEnabled(codes[0])

  return (
    // Wide and tall (Akhil, 2026-09-18: "make the panel bigger so that all 10 items fit"): the
    // results list scrolls within the viewport-capped box rather than `.mb`'s 56vh, and the rows
    // are compact — code, group and units on one line. See `.picker-results` / `.srow.compact`.
    <Modal title={title} onClose={onClose} width={960}>
      {intro ? (
        <div className="pad" style={{ color: 'var(--muted)', fontSize: 12.5 }}>
          {intro}
        </div>
      ) : null}

      <div className="pad">
        <input
          type="text"
          autoFocus
          value={query}
          placeholder="Search catalog… several words narrow it (battery 240)"
          style={{ width: '100%' }}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="picker-results">
        {/* `searching &&` — a disabled query sits at `pending` forever (React Query v5). */}
        {searching && isPending ? <Placeholder>Searching…</Placeholder> : null}
        {/* Names the CALL, not just the message. "500 Internal Server Error" on its own does not
            say which of the three requests this modal makes actually failed, which cost a
            debugging round-trip on 2026-08-12. */}
        {error ? (
          <Placeholder>
            <b>Could not load the catalogue.</b>
            <div style={{ marginTop: 6, fontSize: 12 }}>
              <code>items.get_items</code> failed: {(error as Error).message}
            </div>
          </Placeholder>
        ) : null}
        {/* The price and stock lookups are NOT fatal — the list is still usable without them, so
            they report quietly instead of replacing the results. */}
        {prices.error ? (
          <div className="pad" style={{ fontSize: 11.5, color: 'var(--warn)' }}>
            Prices unavailable ({(prices.error as Error).message}) — the list still works.
          </div>
        ) : null}
        {searching && results.length === 0 && !isPending ? (
          <div className="pad" style={{ color: 'var(--muted)' }}>
            No matches.
            {truncated ? (
              <div style={{ marginTop: 4 }}>
                Searched the first {CATALOG_PAGE} results for <b>{serverQuery}</b> — there may be more.
                Try a more distinctive word.
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Never a silent filter: rows were removed here, not by the server, and possibly more
            exist beyond the page it returned. */}
        {results.length > 0 && terms.length > 1 && (hiddenByTerms > 0 || truncated) ? (
          <div className="pad" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
            Showing rows matching all of <b>{terms.join(' + ')}</b>
            {hiddenByTerms > 0 ? ` — ${hiddenByTerms} other "${serverQuery}" result(s) hidden` : ''}
            {truncated ? `, within the first ${CATALOG_PAGE}` : ''}.
            {/*
              Which catalogue these results came from. The enquiry type scopes the item list
              server-side, but the mapping behind it covers almost nothing — 9 items for
              Provision, 93 for Technical, against 18,836 in total — so a scoped search usually
              finds nothing and `useItems` falls back to the whole catalogue. Saying so is the
              difference between "we widened the search" and a purchaser assuming every item on
              screen is valid for this enquiry type.
            */}
            {data && !data.filtered && enquiryType ? (
              <> Showing the <b>whole catalogue</b> — no {enquiryType} items matched.</>
            ) : null}
          </div>
        ) : null}

        {/*
          The matcher's answers first, and only while the purchaser has not started typing — once
          they search, the results they asked for are what should be at the top.

          Picking one hands back a MINIMAL `Item`: this list carries code, name, group and unit
          but not `uom_conversions`, and `applyItemToLine` re-reads the master by code before
          writing anyway (it needs the full UOM list to correct the line's unit), so nothing
          downstream depends on the gaps here.
        */}
        {/* Named while it loads, so an empty dialog never reads as "no close matches exist". */}
        {query.trim() === '' && suggestedPending && (suggested?.length ?? 0) === 0 ? (
          <div className="pad" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
            Finding the closest matches to what the customer wrote…
          </div>
        ) : null}

        {query.trim() === '' && (suggested?.length ?? 0) > 0 ? (
          <>
            <div className="pad" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              Closest to what the customer wrote:
            </div>
            {suggested!.map((c, index) => (
              <button
                key={`suggested-${c.item_code}`}
                type="button"
                className="srow"
                style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}
                onClick={() =>
                  onPick({
                    code: c.item_code,
                    name: c.item_name,
                    group: c.item_group,
                    base_uom: c.stock_uom,
                    uom_conversions: [],
                    candidate_suppliers: [],
                    contracts: [],
                  })
                }
              >
                <div style={{ minWidth: 0 }}>
                  <div className="nm">{c.item_name || c.item_code}</div>
                  <div className="cd">
                    {c.item_code} · {c.item_group}
                  </div>
                  <SupplierLine group={c.item_group} map={groupSuppliers.data} />
                </div>
                {/* Rate and stock, the same cells the search results below use — this list is
                    where the alternatives are now compared, so it needs the same facts. */}
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: 11.5 }}>
                  <div style={{ color: 'var(--muted)' }}>{Math.round(c.score * 100)}%</div>
                  <PriceCell price={prices.data?.get(c.item_code)} loading={prices.isPending} />
                  <StockCell
                    itemCode={c.item_code}
                    baseUom={c.stock_uom}
                    // The shortlist is ten rows at most and is what the purchaser is choosing
                    // between, so every row of it asks — it is the catalogue list below that is
                    // capped, not this.
                    fetch={stockEnabled.data === true && index < SUGGESTED_STOCK_CAP}
                  />
                </div>
              </button>
            ))}
            <div className="pad" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              …or type above to search the whole catalogue.
            </div>
          </>
        ) : null}
        {!searching && !suggestedPending && (suggested?.length ?? 0) === 0 ? (
          <div className="pad" style={{ fontSize: 12, color: 'var(--muted)' }}>
            Type above to search the catalogue — several words narrow it (<i>battery 240</i>).
          </div>
        ) : null}

        {results.map((item, index) => (
          <button
            key={item.code}
            type="button"
            className="srow compact"
            onClick={() => onPick(item)}
            style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}
          >
            <div style={{ minWidth: 0 }}>
              <div className="nm">{item.name}</div>
              {/* One line: code · group · the units this item can be ordered in — so the choice of
                  item and the choice of unit are made together instead of picking an item and
                  then discovering on the line that it only comes in TRAY. Free: `get_items`
                  already returns `uom_conversions` on every row, so this costs no request. */}
              <div className="cd">
                {item.code} · {item.group} · {unitsLine(item)}
              </div>
              <SupplierLine group={item.group} map={groupSuppliers.data} />
            </div>
            <div style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: 11.5 }}>
              <PriceCell price={prices.data?.get(item.code)} loading={prices.isPending} />
              <StockCell
                itemCode={item.code}
                baseUom={item.base_uom}
                // Only fetched when this deployment can actually answer, and only for the first
                // rows — see `STOCK_ROW_CAP`. Everything else renders `—` and asks nothing.
                fetch={stockEnabled.data === true && index < STOCK_ROW_CAP}
              />
            </div>
          </button>
        ))}

        {/* Never a silent cap: a dash that means "we didn't ask" must not look like one that
            means "none in stock". */}
        {stockEnabled.data === true && results.length > STOCK_ROW_CAP ? (
          <div className="pad" style={{ fontSize: 11, color: 'var(--muted)' }}>
            Stock shown for the first {STOCK_ROW_CAP} results — narrow the search to see it for the rest.
            {' '}(One lookup per item; there is no batch stock endpoint yet.)
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

/**
 * Who can be asked to quote this row.
 *
 * Named suppliers rather than a count: "Al Ghail, Dolphin +5" tells a purchaser whether the item
 * is covered by people they know, which a bare "7 suppliers" does not. Two are shown because the
 * line sits under a description that is already long.
 *
 * "no supplier mapped" is a real and COMMON answer — 44 of 1,240 item groups are mapped and the
 * Frappe team have confirmed the gaps are permanent — so it is stated plainly rather than left
 * blank. It does not block anything: suppliers can still be assigned by hand on the row.
 */
function SupplierLine({ group, map }: { group: string; map: Map<string, string[]> | undefined }) {
  // Nothing at all until the mapping lands — an empty list would read as "none mapped".
  if (!map) return null
  const suppliers = suppliersForGroup(map, group)
  if (suppliers.length === 0) {
    return <div className="cd" style={{ color: 'var(--muted)' }}>no supplier mapped</div>
  }
  const shown = suppliers.slice(0, 2).join(', ')
  const rest = suppliers.length - 2
  return (
    <div className="cd" style={{ color: 'var(--ok)' }} title={suppliers.join('\n')}>
      {shown}
      {rest > 0 ? ` +${rest}` : ''}
    </div>
  )
}

/**
 * The indicative unit price, and WHICH list it came from.
 *
 * The tooltip is not decoration: ten items on this bench have a buying price against 4,253 with
 * only a selling one, so most of what this column shows is a SELLING price. Presented bare it
 * would be read as a cost and quietly wreck a margin decision — `fetchItemPrices` reports the
 * source precisely so the label can be honest about it.
 */
function PriceCell({ price, loading }: { price?: { price: Money; source: PriceSource }; loading: boolean }) {
  if (loading) return <div style={{ color: 'var(--muted)' }}>…</div>
  if (!price) {
    // No `Item Price` row. Distinct from a zero — the catalogue has no price on file, which is a
    // fact about the item, not a price of nothing.
    return <div style={{ color: 'var(--muted)' }}>no price</div>
  }
  return (
    <div
      style={{ fontWeight: 600 }}
      title={`${price.source.buying ? 'Buying' : 'Selling'} price · ${price.source.price_list}`}
    >
      {formatMoney(price.price)}
      {price.source.buying ? null : <span style={{ color: 'var(--muted)', fontWeight: 400 }}> sell</span>}
    </div>
  )
}

/**
 * On-hand quantity — deliberately rendered even though nothing can answer it yet.
 *
 * `items.get_stock` currently returns `{stock_enabled: false, "ERP not configured in Bosun
 * Settings"}` for every item, and this Frappe app has no `Bin` doctype of its own, so the number
 * lives in an ERP that Bosun Settings has not been pointed at. Shown as `—` with the reason on
 * hover rather than hidden: the column was asked for on the understanding that the API fills in
 * later, and a cell that already has its place starts working the day those settings land.
 *
 * `—` NEVER means zero. A zero would earn a red "out of stock" reading this cell has not earned.
 */
function StockCell({ itemCode, baseUom, fetch }: { itemCode: string; baseUom: string; fetch: boolean }) {
  // `useStock` is disabled by an empty code, which is how this stays hook-safe while being
  // conditional in effect — no request for a row we have decided not to ask about.
  const { data, isPending } = useStock(fetch ? itemCode : '')

  if (!fetch) {
    return (
      <div style={{ color: 'var(--muted)' }} title="Stock is not connected yet — no ERP is configured in Bosun Settings">
        — in stock
      </div>
    )
  }
  if (isPending) return <div style={{ color: 'var(--muted)' }}>…</div>
  if (!data?.stock_enabled || data.available === null) {
    return <div style={{ color: 'var(--muted)' }}>— in stock</div>
  }
  /*
    AVAILABLE, not shelf quantity — what is on the shelf minus what is already committed to other
    orders (`toStock`). Choosing an item because it shows 28 in stock, when 33 of those are
    promised elsewhere, is the mistake this column exists to prevent. The shelf and reserved
    figures are on the hover as the explanation.
  */
  /*
    BOTH figures the API gives, not one derived from them.

    `items.get_stock` returns `qty` (on the shelf) and `reserved_qty` (already promised). This cell
    showed only `available` — the subtraction — which reads as a data error when it goes negative
    and hides why. The two raw numbers say what the warehouse actually knows; the row's own
    "exceeds available" warning is where the arithmetic belongs.
  */
  return (
    <div
      style={{ color: data.available > 0 ? 'var(--ok)' : 'var(--danger)', fontWeight: 600 }}
      title={`${data.qty} ${baseUom} on the shelf · ${data.reserved ?? 0} reserved for other orders · ${data.available} available`}
    >
      {data.qty} {baseUom}
      {data.reserved ? (
        <div style={{ fontWeight: 400, fontSize: 11, color: 'var(--warn)' }}>{data.reserved} reserved</div>
      ) : null}
    </div>
  )
}
